import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext.jsx';
import DefaultWebLayout from './layouts/DefaultWebLayout.jsx';
import RbacRoute from './components/RbacRoute.jsx';
import LandingRedirect from './components/LandingRedirect.jsx';
import {
  Unauthorized401,
  Forbidden403,
  NotFound404,
  ServerError500,
} from './pages/status/index.jsx';
import { LoginPage, RegisterPage } from './pages/login/index.jsx';
import './App.scss';

// Access Control, MFA & Compliance Administration (System Administrator console).
const AccessControlAdminPage = lazy(
  () => import('./pages/access-control-mfa-compliance-administration/index.jsx')
);
// User Management (System Administrator) — account CRUD, role assignment, MFA reset.
const UserManagementPage = lazy(() => import('./pages/user-management/index.jsx'));
// Policy & Benefits Configuration (Operations Manager console).
const PolicyBenefitsConfigPage = lazy(
  () => import('./pages/policy-benefits-configuration/index.jsx')
);
// Provider Console — accreditation, roster + WHT, eligibility (Operations Manager).
const ProviderPortalPage = lazy(
  () => import('./pages/provider-portal-dashboard-eligibility/index.jsx')
);
// Corporate HR Roster Dashboard (Corporate Client HR).
const CorporateHrDashboardPage = lazy(
  () => import('./pages/corporate-hr-portal-dashboard/index.jsx')
);
// Member Dashboard + admin profile/eligibility administration (System Administrator).
const MemberDashboardPage = lazy(
  () => import('./pages/member-portal-dashboard/index.jsx')
);
// Request Outpatient Cashless LOA (Member / Policy Holder self-service).
const RequestCashlessLoaPage = lazy(
  () => import('./pages/request-cashless-loa/index.jsx')
);
// Clinical Pre-Auth Queue & Review (Medical Review Officer).
const ClinicalPreAuthQueuePage = lazy(
  () => import('./pages/clinical-pre-auth-queue-review/index.jsx')
);
// Provider Direct-Billing Claims Submission (Healthcare Provider).
const ProviderClaimsSubmissionPage = lazy(
  () => import('./pages/provider-claims-submission/index.jsx')
);
// Submit Cashless LOA (Walk-in) (Healthcare Provider).
const WalkinLoaPage = lazy(
  () => import('./pages/submit-cashless-loa-walk-in/index.jsx')
);
// Submit Dental Treatment Pre-Authorization (Dental Provider).
const SubmitDentalPreAuthPage = lazy(
  () => import('./pages/submit-dental-pre-authorization/index.jsx')
);
// Submit Veterinary Treatment Pre-Authorization (Veterinary Clinic).
const SubmitVetPreAuthPage = lazy(
  () => import('./pages/submit-veterinary-pre-authorization/index.jsx')
);
// Claims Intake & Auditing Queue (Claims Processor).
const ClaimsIntakeQueuePage = lazy(
  () => import('./pages/claims-inbox-auditing-queue/index.jsx')
);
// Claims Adjudication Workspace (Claims Adjudicator).
const ClaimsAdjudicationPage = lazy(
  () => import('./pages/claims-adjudication-workspace/index.jsx')
);
// Claims Appeal & Grievance Resolution (Claims Adjudicator).
const ClaimsAppealResolutionPage = lazy(
  () => import('./pages/claims-appeal-grievance-resolution/index.jsx')
);
// Finance Disbursements & Withholding Tax Panel (Finance User).
const FinanceDisbursementsPage = lazy(
  () => import('./pages/finance-disbursements-tax-panel/index.jsx')
);
// Explanation of Benefits (Member / Policy Holder self-service).
const ExplanationOfBenefitsPage = lazy(
  () => import('./pages/explanation-of-benefits-settlement-breakdown/index.jsx')
);
// File Reimbursement Claim (Member / Policy Holder self-service).
const FileReimbursementClaimPage = lazy(
  () => import('./pages/file-reimbursement-claim/index.jsx')
);
// Member Reimbursement Payout Execution (Finance User).
const MemberPayoutExecutionPage = lazy(
  () => import('./pages/member-reimbursement-payout-execution/index.jsx')
);
// Inpatient Admission GOP Request (Healthcare Provider).
const InpatientGopRequestPage = lazy(
  () => import('./pages/inpatient-admission-gop-request/index.jsx')
);
// Inpatient Stay Authorization Review (Medical Review Officer).
const InpatientStayAuthorizationReviewPage = lazy(
  () => import('./pages/inpatient-stay-authorization-review/index.jsx')
);
// Inpatient Hospital Billing Submission (Healthcare Provider).
const InpatientBillingSubmissionPage = lazy(
  () => import('./pages/inpatient-hospital-billing-submission/index.jsx')
);
// Workflow Rules, Escalations & SLA + AI Auto-Adjudication (Operations Manager).
const WorkflowRulesConfigPage = lazy(
  () => import('./pages/workflow-rules-escalations-sla-configuration/index.jsx')
);
// OCR Claim Intake & Extraction Review (Claims Processor).
const OcrClaimIntakePage = lazy(
  () => import('./pages/ocr-claim-intake-extraction-review/index.jsx')
);
// Coordination of Benefits Review (Claims Processor).
const CoordinationOfBenefitsPage = lazy(
  () => import('./pages/coordination-of-benefits-review/index.jsx')
);
// Fraud, Waste, Abuse & Duplicate Detection (Fraud Analyst).
const FwaDetectionPage = lazy(
  () => import('./pages/fraud-waste-abuse-duplicate-claim-detection/index.jsx')
);
// Communication Gateway & Contact Center Integrations (CSR / Call Center).
const CommunicationIntegrationsPage = lazy(
  () => import('./pages/communication-gateway-contact-center-integrations/index.jsx')
);
// Notification Channels, Templates & Delivery Rules (Operations Manager).
const NotificationRulesPage = lazy(
  () => import('./pages/notification-channels-templates-delivery-rules/index.jsx')
);
// Payout Service Integration Management (Finance User).
const PayoutIntegrationsPage = lazy(
  () => import('./pages/payout-service-integration-management/index.jsx')
);
// Clinical Guidelines & Decision Support Review (Medical Review Officer).
const ClinicalGuidelinesPage = lazy(
  () => import('./pages/clinical-guidelines-decision-support-review/index.jsx')
);
// Carrier Funds & Corporate Billing Reconciliation (Finance User).
const ReconciliationPage = lazy(
  () => import('./pages/carrier-funds-billing-reconciliation/index.jsx')
);
// Customer Service Desk — support ticket logging (CSR).
const CustomerServiceDeskPage = lazy(
  () => import('./pages/customer-service-desk/index.jsx')
);
// Financial, Claims Paid & Reconciliation Reports (Finance User).
const FinanceReportsPage = lazy(
  () => import('./pages/financial-claims-reconciliation-reports/index.jsx')
);
// Operational SLA, Queue & Turnaround Reports (Operations Manager).
const OpsReportsPage = lazy(
  () => import('./pages/operational-sla-turnaround-reports/index.jsx')
);
// Regulatory, Audit & Compliance Reports (Compliance Officer).
const ComplianceReportsPage = lazy(
  () => import('./pages/regulatory-compliance-reports/index.jsx')
);
// Carrier Program Performance Dashboard (Insurance Company Representative).
const CarrierDashboardPage = lazy(
  () => import('./pages/carrier-program-performance-dashboard/index.jsx')
);
// Provider Performance, Utilization & Service Quality Monitor (Provider Relations Officer).
const ProviderScorecardsPage = lazy(
  () => import('./pages/provider-performance-utilization-service-quality-m/index.jsx')
);
// AI / LLM Provider Settings (System Administrator).
const AiLlmProviderSettingsPage = lazy(
  () => import('./pages/ai-llm-provider-settings/index.jsx')
);

// Pages are generated per page by ui-components-from-reference and wired here,
// keyed on their slug (see config/menuData.js). Until a page's component is
// generated, its menu link routes to `/{slug}` and falls through to the
// catch-all 404 below, so the navigation never dead-ends.
//
//   const Dashboard = lazy(() => import('./pages/member-portal-dashboard/index.jsx'));
//   <Route path="member-portal-dashboard" element={<Dashboard />} />

const Loader = () => (
  <div className="d-flex justify-content-center py-5">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading…</span>
    </div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<Loader />}>
          <Routes>
            {/* Public authentication pages — standalone, full-bleed (no app
                chrome): the OS-series split-panel owns the whole viewport, so
                they sit OUTSIDE DefaultWebLayout (no navbar/footer). */}
            <Route path="login" element={<LoginPage />} />
            <Route path="register" element={<RegisterPage />} />

            <Route element={<DefaultWebLayout />}>
              {/* Landing → role-aware home (no spurious 403 for non-admins) */}
              <Route index element={<LandingRedirect />} />

              {/* Generic status pages */}
              <Route path="401" element={<Unauthorized401 />} />
              <Route path="403" element={<Forbidden403 />} />
              <Route path="500" element={<ServerError500 />} />

              {/* Role-gated consoles — each route is checked against the
                  PAGE_ROLES matrix by RbacRoute (deny by default). */}
              <Route element={<RbacRoute />}>
                <Route path="user-management" element={<UserManagementPage />} />
                <Route
                  path="access-control-mfa-compliance-administration"
                  element={<AccessControlAdminPage />}
                />
                <Route
                  path="ai-llm-provider-settings"
                  element={<AiLlmProviderSettingsPage />}
                />
                <Route
                  path="policy-benefits-configuration"
                  element={<PolicyBenefitsConfigPage />}
                />
                <Route
                  path="provider-portal-dashboard-eligibility"
                  element={<ProviderPortalPage />}
                />
                <Route
                  path="corporate-hr-portal-dashboard"
                  element={<CorporateHrDashboardPage />}
                />
                <Route
                  path="member-portal-dashboard"
                  element={<MemberDashboardPage />}
                />
                <Route
                  path="clinical-pre-auth-queue-review"
                  element={<ClinicalPreAuthQueuePage />}
                />
                <Route
                  path="claims-inbox-auditing-queue"
                  element={<ClaimsIntakeQueuePage />}
                />
                <Route
                  path="claims-adjudication-workspace"
                  element={<ClaimsAdjudicationPage />}
                />
                <Route
                  path="claims-appeal-grievance-resolution"
                  element={<ClaimsAppealResolutionPage />}
                />
                <Route
                  path="finance-disbursements-tax-panel"
                  element={<FinanceDisbursementsPage />}
                />
                <Route
                  path="member-reimbursement-payout-execution"
                  element={<MemberPayoutExecutionPage />}
                />
                <Route
                  path="inpatient-stay-authorization-review"
                  element={<InpatientStayAuthorizationReviewPage />}
                />
                <Route
                  path="workflow-rules-escalations-sla-configuration"
                  element={<WorkflowRulesConfigPage />}
                />
                <Route
                  path="ocr-claim-intake-extraction-review"
                  element={<OcrClaimIntakePage />}
                />
                <Route
                  path="coordination-of-benefits-review"
                  element={<CoordinationOfBenefitsPage />}
                />
                <Route
                  path="fraud-waste-abuse-duplicate-claim-detection"
                  element={<FwaDetectionPage />}
                />
                <Route
                  path="communication-gateway-contact-center-integrations"
                  element={<CommunicationIntegrationsPage />}
                />
                <Route
                  path="notification-channels-templates-delivery-rules"
                  element={<NotificationRulesPage />}
                />
                <Route
                  path="payout-service-integration-management"
                  element={<PayoutIntegrationsPage />}
                />
                <Route
                  path="clinical-guidelines-decision-support-review"
                  element={<ClinicalGuidelinesPage />}
                />
                <Route
                  path="carrier-funds-corporate-billing-reconciliation"
                  element={<ReconciliationPage />}
                />
                <Route
                  path="customer-service-desk"
                  element={<CustomerServiceDeskPage />}
                />
                <Route
                  path="financial-claims-paid-reconciliation-reports"
                  element={<FinanceReportsPage />}
                />
                <Route
                  path="operational-sla-queue-turnaround-reports"
                  element={<OpsReportsPage />}
                />
                <Route
                  path="regulatory-audit-compliance-reports"
                  element={<ComplianceReportsPage />}
                />
                <Route
                  path="carrier-program-performance-dashboard"
                  element={<CarrierDashboardPage />}
                />
                <Route
                  path="provider-performance-utilization-service-quality-m"
                  element={<ProviderScorecardsPage />}
                />
              </Route>

              {/* Member / provider self-service — role-gated via the same matrix. */}
              <Route element={<RbacRoute />}>
                <Route path="request-cashless-loa" element={<RequestCashlessLoaPage />} />
                <Route path="provider-claims-submission" element={<ProviderClaimsSubmissionPage />} />
                <Route path="submit-cashless-loa-walk-in" element={<WalkinLoaPage />} />
                <Route path="submit-dental-pre-authorization" element={<SubmitDentalPreAuthPage />} />
                <Route path="submit-veterinary-pre-authorization" element={<SubmitVetPreAuthPage />} />
                <Route path="explanation-of-benefits-settlement-breakdown" element={<ExplanationOfBenefitsPage />} />
                <Route path="file-reimbursement-claim" element={<FileReimbursementClaimPage />} />
                <Route path="inpatient-admission-gop-request" element={<InpatientGopRequestPage />} />
                <Route path="inpatient-hospital-billing-submission" element={<InpatientBillingSubmissionPage />} />
              </Route>

              {/* Generated page routes are inserted above this line, keyed by slug. */}

              {/* Catch-all → 404 (placeholder target for not-yet-built pages). */}
              <Route path="*" element={<NotFound404 />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
